import { useQuery } from '@tanstack/react-query';
import { API_CONFIG } from '@/lib/api';

/**
 * Shared fetch policy for every /analytics/* query. One object so the five hooks
 * below can't drift — the previous copy-pasted blocks all carried staleTime: 0.
 *
 * `staleTime` is the fix for the duplicate `/analytics/overview` call seen on the
 * deployed build, and it is the ONLY value that changed. Focus refetching is
 * freshness-gated: at 0 the response was stale the instant it landed, so the
 * focus/visibilitychange event a browser raises moments after load fired a
 * second, byte-identical request as the first resolved. Do NOT set it back to 0.
 * (Same gating applies to refetchOnReconnect, on by default.)
 *
 * `refetchOnMount: 'always'` is DELIBERATE — analytics figures move constantly,
 * so every page entry must fetch current numbers, never a cached snapshot. It
 * ignores staleTime by design, and is safe because a duplicate needs two MOUNTS:
 * each of these pages has a single route, no redirect chain and no key= remount,
 * and React Query dedupes concurrent observers of one key into one request.
 *
 * Unaffected by staleTime: refresh / new tab (drops the in-memory cache) and a
 * range change / account switch (different query key). These queries never poll
 * — no refetchInterval, no socket invalidation — so if a genuinely live figure is
 * ever needed, add refetchInterval on that page's hook; staleTime: 0 does not
 * buy live data, it only re-fetches at the same moments, twice.
 */
const ANALYTICS_QUERY_OPTIONS = {
  staleTime: 15 * 1000,
  refetchOnMount: 'always',
  refetchOnWindowFocus: true,
} as const;

export function useMessageAnalytics(businessAccountId?: string, dateRange?: { start: string; end: string }) {
  return useQuery({
    queryKey: ['message-analytics', businessAccountId, dateRange],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (businessAccountId) params.append('businessAccountId', businessAccountId);
      if (dateRange?.start) params.append('startDate', dateRange.start);
      if (dateRange?.end) params.append('endDate', dateRange.end);

      const token = localStorage.getItem('auth_token');
      const headers: Record<string, string> = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${API_CONFIG.BASE_URL}/analytics/messages?${params.toString()}`, {
        headers,
      });
      if (!response.ok) throw new Error('Failed to fetch message analytics');
      
      const result = await response.json();
      return result.data;
    },
    enabled: !!businessAccountId,
    ...ANALYTICS_QUERY_OPTIONS,
  });
}

export function useConversationAnalytics(businessAccountId?: string, dateRange?: { start: string; end: string }) {
  return useQuery({
    queryKey: ['conversation-analytics', businessAccountId, dateRange],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (businessAccountId) params.append('businessAccountId', businessAccountId);
      if (dateRange?.start) params.append('startDate', dateRange.start);
      if (dateRange?.end) params.append('endDate', dateRange.end);

      const token = localStorage.getItem('auth_token');
      const headers: Record<string, string> = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${API_CONFIG.BASE_URL}/analytics/conversations?${params.toString()}`, {
        headers,
      });
      if (!response.ok) throw new Error('Failed to fetch conversation analytics');

      const result = await response.json();
      return result.data;
    },
    enabled: !!businessAccountId,
    ...ANALYTICS_QUERY_OPTIONS,
  });
}

export function useCampaignAnalytics(businessAccountId?: string, dateRange?: { start: string; end: string }) {
  return useQuery({
    queryKey: ['campaign-analytics', businessAccountId, dateRange],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (businessAccountId) params.append('businessAccountId', businessAccountId);
      if (dateRange?.start) params.append('startDate', dateRange.start);
      if (dateRange?.end) params.append('endDate', dateRange.end);

      const token = localStorage.getItem('auth_token');
      const headers: Record<string, string> = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${API_CONFIG.BASE_URL}/analytics/campaigns?${params.toString()}`, {
        headers,
      });
      if (!response.ok) throw new Error('Failed to fetch campaign analytics');

      const result = await response.json();
      return result.data;
    },
    enabled: !!businessAccountId,
    ...ANALYTICS_QUERY_OPTIONS,
  });
}

export function useAgentPerformance(businessAccountId?: string, dateRange?: { start: string; end: string }) {
  return useQuery({
    queryKey: ['agent-performance', businessAccountId, dateRange],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (businessAccountId) params.append('businessAccountId', businessAccountId);
      if (dateRange?.start) params.append('startDate', dateRange.start);
      if (dateRange?.end) params.append('endDate', dateRange.end);

      const token = localStorage.getItem('auth_token');
      const headers: Record<string, string> = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${API_CONFIG.BASE_URL}/analytics/agents?${params.toString()}`, {
        headers,
      });
      if (!response.ok) throw new Error('Failed to fetch agent analytics');

      const result = await response.json();
      return result.data;
    },
    enabled: !!businessAccountId,
    ...ANALYTICS_QUERY_OPTIONS,
  });
}

export function useOverviewAnalytics(businessAccountId?: string, dateRange?: { start: string; end: string }) {
  return useQuery({
    queryKey: ['overview-analytics', businessAccountId, dateRange],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (businessAccountId) params.append('businessAccountId', businessAccountId);
      if (dateRange?.start) params.append('startDate', dateRange.start);
      if (dateRange?.end) params.append('endDate', dateRange.end);

      const token = localStorage.getItem('auth_token');
      const headers: Record<string, string> = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${API_CONFIG.BASE_URL}/analytics/overview?${params.toString()}`, {
        headers,
      });
      if (!response.ok) throw new Error('Failed to fetch overview analytics');
      
      const result = await response.json();
      return result.data;
    },
    enabled: !!businessAccountId,
    ...ANALYTICS_QUERY_OPTIONS,
  });
}
